import React, { useMemo, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import ClientStateDistributionCard from './ClientStateDistributionCard';
import ProspectStateDistributionCard from './ProspectStateDistributionCard';
import StateDistributionFilters from './StateDistributionFilters';
import {
  buildClientStateCounts,
  buildClientHistoricalStateCounts,
  buildProspectHistoricalStateCounts,
  buildDistributionRows,
  filterClients,
  filterProspects,
  withPercentages,
} from '../../utils/state';
import {
  buildClientDetailExportRows,
  buildProspectDetailExportRows,
  getClientDetailColumns,
  getProspectDetailColumns,
} from '../../utils/state';
import {
  exportStateDistributionExcel,
  exportStateDistributionPdf,
} from '../../utils/state';
import { buildProspectStateCounts } from '../../utils/analytics';
import { prospectStatusLabel } from '../../utils/prospectStatus';

function slugify(value) {
  return String(value || 'distribution')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
}

function FilterableStateDistribution({
  variant = 'client',
  title,
  clients = [],
  prospects = [],
  fallbackStateData = {},
  labels = {},
  prospectStatuses = [],
  t,
  vendors = [],
  showVendorFilter = false,
  filterLabels = {},
  delay = 0,
  footer,
  loading = false,
}) {
  const { locale } = useLanguage();
  const { user } = useAuth();
  const [dateFrom, setDateFrom] = useState(null);
  const [dateTo, setDateTo] = useState(null);
  const [vendorId, setVendorId] = useState('');
  const [historicalMode, setHistoricalMode] = useState(false);
  const [exporting, setExporting] = useState(false);
  const cardRef = useRef(null);

  const filteredRecords = useMemo(() => {
    if (variant === 'client') {
      if (clients.length === 0) return [];
      return filterClients(clients, { dateFrom, dateTo, vendorId }, vendors);
    }
    if (prospects.length === 0) return [];
    return filterProspects(prospects, { dateFrom, dateTo, vendorId });
  }, [variant, clients, prospects, dateFrom, dateTo, vendorId, vendors]);

  const stateData = useMemo(() => {
    if (variant === 'client') {
      if (clients.length > 0) {
        if (historicalMode) {
          return buildClientHistoricalStateCounts(clients, { dateFrom, dateTo, vendorId }, vendors);
        }
        return buildClientStateCounts(filteredRecords);
      }
      return fallbackStateData;
    }

    if (prospects.length > 0) {
      if (historicalMode) {
        return buildProspectHistoricalStateCounts(prospects, { dateFrom, dateTo, vendorId });
      }
      return buildProspectStateCounts(filteredRecords);
    }
    return fallbackStateData;
  }, [
    variant,
    clients,
    prospects,
    filteredRecords,
    fallbackStateData,
    historicalMode,
    dateFrom,
    dateTo,
    vendorId,
    vendors,
  ]);

  const exportRows = useMemo(() => {
    if (variant === 'client') {
      const clientLabels = {
        affiliated: labels.affiliated,
        contacted: labels.contacted,
        visited: labels.visited,
        completed: labels.completed,
      };
      return withPercentages(
        buildDistributionRows(stateData, clientLabels).filter((row) =>
          ['affiliated', 'contacted', 'visited', 'completed'].includes(row.key)
        )
      );
    }

    const prospectLabels = prospectStatuses.reduce((acc, status) => {
      const code = String(status.code || '').trim().toUpperCase();
      if (!code) return acc;
      acc[code] = prospectStatusLabel(status.code, t, prospectStatuses);
      return acc;
    }, {});

    const rows = Object.entries(stateData).map(([code, count]) => ({
      key: code,
      label: prospectLabels[code] || code,
      count: count || 0,
    }));

    return withPercentages(rows);
  }, [variant, stateData, labels, prospectStatuses, t]);

  const detailColumns = useMemo(() => {
    if (variant === 'client') {
      return getClientDetailColumns(filterLabels, showVendorFilter);
    }
    return getProspectDetailColumns(filterLabels, showVendorFilter);
  }, [variant, filterLabels, showVendorFilter]);

  const detailRecords = useMemo(() => {
    if (variant === 'client') {
      return buildClientDetailExportRows(filteredRecords, {
        labels,
        locale,
        showVendor: showVendorFilter,
      });
    }
    return buildProspectDetailExportRows(filteredRecords, {
      t,
      prospectStatuses,
      locale,
      showVendor: showVendorFilter,
    });
  }, [variant, filteredRecords, labels, locale, showVendorFilter, t, prospectStatuses]);

  const selectedVendor = vendors.find((vendor) => String(vendor.id) === String(vendorId));

  async function handleExport(type) {
    const hasSummary = exportRows.some((row) => (row.count || 0) > 0);
    if (!hasSummary && detailRecords.length === 0) {
      toast.error(filterLabels.noData || 'No data to export');
      return;
    }

    setExporting(true);
    try {
      const baseName = slugify(title);
      const payload = {
        title,
        rows: exportRows,
        filters: {
          dateFrom,
          dateTo,
          vendorLabel: selectedVendor?.username || null,
          historicalMode,
        },
        labels: filterLabels,
        locale,
        printedBy: user?.username || '',
        filename: `${baseName}.${type === 'pdf' ? 'pdf' : 'xlsx'}`,
        detailColumns,
        detailRecords,
      };

      if (type === 'pdf') {
        await exportStateDistributionPdf({
          ...payload,
          element: cardRef.current,
        });
      } else {
        exportStateDistributionExcel(payload);
      }
      toast.success(filterLabels.exportSuccess || 'Export completed');
    } catch (error) {
      console.error('State distribution export failed:', error);
      toast.error(filterLabels.exportFailed || 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  return (
    <div className="w-full space-y-4">
      <StateDistributionFilters
        labels={filterLabels}
        dateFrom={dateFrom}
        dateTo={dateTo}
        onDateFromChange={setDateFrom}
        onDateToChange={setDateTo}
        vendorId={vendorId}
        onVendorChange={setVendorId}
        vendors={vendors}
        showVendorFilter={showVendorFilter}
        exporting={exporting}
        onExportPdf={() => handleExport('pdf')}
        onExportExcel={() => handleExport('excel')}
        historicalMode={historicalMode}
        onHistoricalModeChange={setHistoricalMode}
      />

      {loading ? (
        <p className="text-sm text-secondary-500 ">
          {filterLabels.loading || 'Loading...'}
        </p>
      ) : null}

      {variant === 'client' ? (
        <div ref={cardRef} id={`state-distribution-card-${slugify(title)}`}>
          <ClientStateDistributionCard
            title={title}
            stateData={stateData}
            labels={labels}
            footer={footer}
            delay={delay}
          />
        </div>
      ) : (
        <div ref={cardRef} id={`state-distribution-card-${slugify(title)}`}>
          <ProspectStateDistributionCard
            title={title}
            stateData={stateData}
            prospectStatuses={prospectStatuses}
            t={t}
            delay={delay}
          />
        </div>
      )}
    </div>
  );
}

export default FilterableStateDistribution;
